import _ from 'lodash';
import constants from '#packages/constants';
import { overridable } from '@wix/santa-editor-utils';
import * as highlightsSelectors from './highlightsSelectors';
import * as selectionSelectors from '../selection/selectionSelectors';
import * as mouseActions from '../mouseActions/mouseActions';
import highlightsActionTypes from './highlightsActionTypes';
import type { CompRef, DSRead } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { ThunkAction } from 'types/redux';
import type { CompLayout } from '@wix/document-services-types';

function getItemHighlight(
  editorAPI: EditorAPI,
  compRef: CompRef,
  withOpacity: boolean,
) {
  return {
    compRef,
    layout: editorAPI.components.layout.getRelativeToScreen(compRef),
    type: withOpacity
      ? constants.UI.HIGHLIGHTS.TYPES.THICK_WITH_OPACITY
      : constants.UI.HIGHLIGHTS.TYPES.THICK,
  };
}

const getHighlightStructure = (
  compRef: CompRef,
  layout: CompLayout,
  type: string,
) => ({
  compRef,
  layout,
  type,
});

const {
  ADD_TO_HIGHLIGHTS,
  REMOVE_COMPS_FROM_HIGHLIGHTS,
  SET_HIGHLIGHTS,
  CLEAR_HIGHLIGHTS,
  REMOVE_FROM_HIGHLIGHTS_BY_HIGHLIGHT_TYPE,
} = highlightsActionTypes;

interface HighlightI {
  compRef: CompRef;
  layout: CompLayout;
  type: string;
  displayNameOverride?: string;
}

const addToHighlights = (highlightsDefs: HighlightI[]) => ({
  type: ADD_TO_HIGHLIGHTS,
  highlightsDefs,
});

const addCompsToHighlights =
  (compRefs: CompRef[], highlightType: string): ThunkAction =>
  (dispatch, getState, { editorAPI }) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/map
    const highlightsDefs = _.map(compRefs, (compRef) => ({
      compRef,
      layout: editorAPI.components.layout.getRelativeToScreen(compRef),
      type: highlightType,
    }));

    dispatch(addToHighlights(highlightsDefs));
  };

const addExternalHighlight =
  (compRef: CompRef): ThunkAction =>
  (dispatch, getState, { dsRead }) =>
    dispatch(
      addToHighlights([
        {
          compRef,
          layout: dsRead.components.layout.getRelativeToScreen(compRef),
          // TODO: Fix
          // @ts-expect-error
          type: constants.UI.HIGHLIGHTS.TYPES.EXTERNAL,
        },
      ]),
    );

const removeCompsFromHighlights = overridable((compRefs: CompRef[]) => ({
  type: REMOVE_COMPS_FROM_HIGHLIGHTS,
  compRefs,
}));

const clearExternalHighlights = (): ThunkAction => (dispatch) =>
  dispatch(
    removeFromHighlightsByHighlightType([
      // TODO: Fix
      // @ts-expect-error
      constants.UI.HIGHLIGHTS.TYPES.EXTERNAL,
    ]),
  );

const clearOverlayHighlights = (): ThunkAction => (dispatch) =>
  dispatch(
    removeFromHighlightsByHighlightType([
      constants.UI.HIGHLIGHTS.TYPES.OVERLAY,
    ]),
  );

const setHighlights = (highlightsDefs: HighlightI[]) => ({
  type: SET_HIGHLIGHTS,
  highlightsDefs,
});

const clearHighlights = () => ({
  type: CLEAR_HIGHLIGHTS,
});

const removeFromHighlightsByHighlightType = (highlightTypes: string[]) => ({
  type: REMOVE_FROM_HIGHLIGHTS_BY_HIGHLIGHT_TYPE,
  highlightTypes,
});

const { isPerformingMouseMoveAction } = mouseActions.selectors;

const getPlatformConnectionToHighlight = (dsRead: DSRead, compRef: CompRef) => {
  const connections = dsRead.platform.controllers.connections.get(compRef);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/is-array
  if (_.isArray(connections)) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/find
    const compConnection = _.find(connections, (connection) => {
      const { controllerRef, role } = connection;
      const stageDataByRole = dsRead.platform.controllers.getStageDataByRole(
        controllerRef,
        role,
      );
      return stageDataByRole?.relatedToRole;
    });
    if (compConnection) {
      // TODO fix this
      // @ts-expect-error
      const { controllerRef, role } = compConnection;
      const connectedComponents =
        dsRead.platform.controllers.connections.getConnectedComponents(
          controllerRef,
        );
      const roleConnection = dsRead.platform.controllers.getStageDataByRole(
        controllerRef,
        role,
      );
      const connectionsGet = dsRead.platform.controllers.connections.get;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/filter
      return _.filter(
        connectedComponents,
        (connectedRef) =>
          _.head(connectionsGet(connectedRef)).role ===
            roleConnection.relatedToRole &&
          dsRead.components.is.rendered(connectedRef) &&
          dsRead.components.is.visible(connectedRef),
      );
    }
  }
};

const handleSelectionHighlights = overridable(
  (): ThunkAction =>
    (dispatch, getState, { dsRead, editorAPI }) => {
      const highlightsDefs = [] as HighlightI[];
      const selectedComponents =
        selectionSelectors.getSelectedCompsRefs(getState());
      const selectedComponent = _.head(selectedComponents);

      dispatch(
        removeFromHighlightsByHighlightType([
          constants.UI.HIGHLIGHTS.TYPES.THICK_WITH_OPACITY,
          constants.UI.HIGHLIGHTS.TYPES.THICK,
        ]),
      );

      if (!selectedComponent) {
        return;
      }

      const platformRelatedCompRefs = getPlatformConnectionToHighlight(
        dsRead,
        selectedComponent,
      );
      if (!_.isEmpty(platformRelatedCompRefs)) {
        platformRelatedCompRefs.forEach((compRef) =>
          highlightsDefs.push(getItemHighlight(editorAPI, compRef, false)),
        );
        dispatch(addToHighlights(highlightsDefs));
        return;
      }
      const isRepeaterItem = editorAPI.components.is.repeater(
        dsRead.deprecatedOldBadPerformanceApis.components.getContainer(
          selectedComponent,
        ),
      );

      if (isRepeaterItem) {
        highlightsDefs.push(
          getItemHighlight(editorAPI, selectedComponent, false),
        );
        dispatch(addToHighlights(highlightsDefs));
        return;
      }

      const repeaterItemRef = editorAPI.components.findAncestor(
        selectedComponent,
        (ancestor) =>
          editorAPI.components.is.repeater(
            editorAPI.components.getContainer(ancestor),
          ),
        { includeScopeOwner: true },
      );

      if (repeaterItemRef) {
        highlightsDefs.push(getItemHighlight(editorAPI, repeaterItemRef, true));

        dispatch(addToHighlights(highlightsDefs));
      }
    },
);

const handleComponentHighlight =
  (
    compRef: CompRef,
    layout: CompLayout,
    isHover: boolean,
    type?: string,
  ): ThunkAction =>
  (dispatch) => {
    const highlightsDefs = [];
    const defaultHighlightType = isHover
      ? constants.UI.HIGHLIGHTS.TYPES.OVERLAY_WITH_OPACITY
      : constants.UI.HIGHLIGHTS.TYPES.OVERLAY;
    const highlightType = type ? type : defaultHighlightType;

    dispatch(removeFromHighlightsByHighlightType([highlightType]));
    highlightsDefs.push(getHighlightStructure(compRef, layout, highlightType));
    dispatch(addToHighlights(highlightsDefs));
  };

const handleMultiComponentHighlight =
  (
    compRefsWithLayout: { compRef: CompRef; layout: CompLayout }[],
    isHover: boolean,
    type: string,
  ): ThunkAction =>
  (dispatch) => {
    const highlightsDefs = [] as HighlightI[];
    const defaultHighlightType = isHover
      ? constants.UI.HIGHLIGHTS.TYPES.OVERLAY_WITH_OPACITY
      : constants.UI.HIGHLIGHTS.TYPES.OVERLAY;

    const highlightType = type ? type : defaultHighlightType;

    dispatch(removeFromHighlightsByHighlightType([highlightType]));
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
    _.forEach(compRefsWithLayout, ({ compRef, layout }) => {
      highlightsDefs.push(
        getHighlightStructure(compRef, layout, highlightType),
      );
    });

    dispatch(addToHighlights(highlightsDefs));
  };

const updateHighlights =
  (): ThunkAction =>
  (dispatch, getState, { dsRead, editorAPI }) => {
    const state = getState();
    const highlights = highlightsSelectors.getHighlights(state);

    if (_.isEmpty(highlights) || isPerformingMouseMoveAction(state)) {
      return;
    }

    const isHighlightCompRefExist = ({ compRef }: { compRef: CompRef }) =>
      dsRead.components.is.exist(compRef);
    const highlightDefWithLayout = (highlightDef: HighlightI) =>
      _.defaults(
        {
          layout: getComponentLayout(highlightDef.compRef, editorAPI),
        },
        highlightDef,
      );

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/filter
    const updatedHighlights = _(highlights)
      .filter(isHighlightCompRefExist)
      .map(highlightDefWithLayout)
      .value();

    // TODO: Fix
    // @ts-expect-error
    dispatch(setHighlights(updatedHighlights));
  };

const getComponentLayout = (compRef: CompRef, editorAPI: EditorAPI) => {
  if (!editorAPI.zoomMode.isStageZoomMode()) {
    return editorAPI.components.layout.getRelativeToScreen(compRef);
  }

  const boundingRect =
    editorAPI.components.layout.measure.getBoundingClientRect(compRef);
  return {
    ...boundingRect,
    y: boundingRect.absoluteTop,
    x: boundingRect.absoluteLeft,
  };
};

export {
  addToHighlights,
  addCompsToHighlights,
  addExternalHighlight,
  clearExternalHighlights,
  removeCompsFromHighlights,
  removeFromHighlightsByHighlightType,
  setHighlights,
  clearHighlights,
  clearOverlayHighlights,
  handleSelectionHighlights,
  handleComponentHighlight,
  updateHighlights,
  handleMultiComponentHighlight,
  getComponentLayout,
};
